import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

// "Back" on a player's or a team's page. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// BACK GOES BACK WHEN THIS VISIT PUSHED THE PAGE, AND TO THE LIST WHEN IT DID NOT
// ═══════════════════════════════════════════════════════════════════════════
//
// The design's Back is a button that names where it goes (`Players`, `Leaders`, a team's name) and returns there. With
// real paths there are two ways a member arrives at a player's page: by tapping a row (this app pushed an entry, and the
// entry before it is the list, with its season and its scroll), or by opening a link someone pasted (the entry before
// it is whatever they were doing before, possibly another site).
//
// So a link INSIDE the season views carries `state: { hoopsBack: 'Leaders' }` (the label of where it came from), and:
//
//   state has a label     the entry before this one is a season view this app put there. Back is `navigate(-1)`, the
//                         same as the phone's own gesture, and the button is named for the label.
//   no state              a direct visit. Back goes to the list the page belongs to, with REPLACE, so the page does
//                         not sit in history behind the list it just went to (a Back that lands on the page you
//                         left and bounces forward again is a trap, `App.jsx`'s REDIRECTS says the same).
//
// `navigate(-1)` FROM AN ENTRY THIS APP DID NOT CREATE WOULD LEAVE THE SITE. That is the exact hazard
// `HoopsPanel.jsx`'s game selection documents for "← Tonight", and the same answer: only trust the history when it is
// known to be ours. The state is what makes it known, and it survives a reload with the entry.
//
// A SEASON CHANGE KEEPS THE LABEL (`historyStore.js::useSeasonChoice` passes the state along when it replaces the
// address), or choosing 2024-25 on a player's page would turn "Back to Leaders" into "Back to Players" while the entry
// behind it was still Leaders.

/** The label a link into a detail page carries: where it came from. */
export const BACK_KEY = 'hoopsBack'

/** The state a link into a detail page passes: `<Link state={backState('Leaders')}>`. */
export const backState = (label) => ({ [BACK_KEY]: label })

/**
 * `{ label, onBack }` for a detail page: `label` is where Back goes, `onBack` takes it there. `fallbackLabel` and
 * `fallbackTo` are the list the page belongs to, used when there is no entry of ours to go back to.
 */
export function useBack(fallbackLabel, fallbackTo) {
  const location = useLocation()
  const navigate = useNavigate()
  const from = location.state?.[BACK_KEY]
  const known = typeof from === 'string' && from !== ''
  const onBack = useCallback(() => {
    if (known) navigate(-1)
    else navigate(fallbackTo, { replace: true })
  }, [known, navigate, fallbackTo])
  return { label: known ? from : fallbackLabel, onBack }
}
